import { DataTableValidator, ColumnDef } from './data-table.interface';
import { Validators, AbstractControl } from '@angular/forms';

// EMAIL REGEX Used by Angular for form controls
const EMAIL_REGEXP =
    /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const ALPHANUMERIC_REGEXP =
    /^[a-zA-Z0-9_]*$/;

const NUMERIC_REGEXP =
    /^[0-9]*$/;

const NEGATIVE_NUMERIC_REGEXP =
    /^-?[0-9]*$/;

export class DataTableValidators {

    public static maxLength(num: number, callback: (message: string) => void = () => ({})): DataTableValidator {
        return {
            validator: (data: string) => data.length <= num,
            invalid: (column: ColumnDef) => callback(`The input for column '${column.name}' was too long. (Max Length: ${num} characters)`),
            snippit: `Max Length: ${num} characters`
        };
    }

    public static minLength(num: number, callback: (message: string) => void = () => ({})): DataTableValidator {
        return {
            validator: (data: string) => data.length >= num,
            invalid: (column: ColumnDef) => callback(`The input for column '${column.name}' was too short. (Min Length: ${num} characters)`),
            snippit: `Min Length: ${num} characters`
        };
    }

    public static required(callback: (message: string) => void = () => ({})) {
        return {
            validator: (data: string) => data && data.length > 0,
            invalid: (column: ColumnDef) => callback(`Please provide some input for '${column.name}'`),
            snippit: 'Required'
        };
    }

    public static email(callback: (message: string) => void = () => ({})) {
        return {
            validator: (data: string) => {
                // Allow empty emails
                if (data.length === 0) {
                    return true;
                }

                return EMAIL_REGEXP.test(data);
            },
            invalid: (column: ColumnDef) => callback(`Not a valid email for ${column.name}`),
            snippit: 'Invalid Email'
        };
    }

    public static alphaNumeric(callback: (message: string) => void = () => ({})) {
        return {
            validator: (data: string) => ALPHANUMERIC_REGEXP.test(data),
            invalid: (column: ColumnDef) => callback(`Not a valida alpha-numeric input for ${column.name}`),
            snippit: 'Must be alpha-numeric'
        };
    }

    public static numeric(negatives = false, callback: (message: string) => void = () => ({})) {
        return {
            validator: (data: string) => {
                const num = +(data.trim());
                return !isNaN(num) && (negatives || num >= 0);
            },
            invalid: (column: ColumnDef) => callback(`Not a valid${negatives ? '' : ' positive '} numeric input for ${column.name}`),
            snippit: `Must be a${negatives ? '' : ' positive '} number`
        };
    }

    public static notEqual(match: string, matchCase: boolean, callback: (message: string) => void = () => ({})) {
        return {
            validator: (data: string) => match.localeCompare(data, undefined, { sensitivity: matchCase ? 'case' : 'base' }) !== 0,
            invalid: (column: ColumnDef) => callback(`${column.name} cannot be '${match}'`),
            snippit: `Cannot be ${match}`
        };
    }

    public static numericAndRange(callback: (message: string) => void = () => ({}), min = -99, max = 99) {
        return {
            validator: (data: number) => data >= min && data <= max,
            invalid: (column: ColumnDef) => callback(`${column.name}: Invalid range`),
            snippit: `Must be greater than ${min} and less than ${max}`
        };
    }

    public static regex(regex: string, badRegexMessage?: string, callback: (message: string) => void = () => ({})) {
        const regexp = new RegExp(regex);
        return {
            validator: (data: string) => regexp.test(data),
            invalid: (column: ColumnDef) => callback(`${column.name}: Invalid input. Must match '${regex}'`),
            snippit: badRegexMessage || `Must match the expression ${regex}`
        };
    }
}